import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { MainHeader } from '../components/MainHeader';
import { PrimaryButton } from '../components/PrimaryButton';
import { SecondaryButton } from '../components/SecondaryButton';
import { CancelBookingModal } from '../components/CancelBookingModal';
import { RescheduleModal } from '../components/RescheduleModal';
import { ReportProblemModal } from '../components/ReportProblemModal';
import { Footer } from '../components/Footer';
import { LeaveReviewModal } from '../components/LeaveReviewModal';
import {
  cancelBooking,
  getBookingDetail,
  getBookingReview,
  getCancellationPreview,
  reportBookingProblem,
  submitBookingReview,
  type BookingReview,
  type CancellationPreview,
} from '../../lib/booking-api';
import { customerApiErrorMessage } from '../../lib/customer-api';
import { toast } from '../../lib/toast';
import { toAllInBreakdown } from '../../lib/all-in-breakdown';

function formatMoney(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
    }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(2)}`;
  }
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

function joinName(first: string | null, last: string | null): string {
  return [first, last].filter(Boolean).join(' ') || '—';
}

export default function BookingDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [info, setInfo] = useState<any | null>(null);
  const [review, setReview] = useState<BookingReview | null>(null);
  const [loading, setLoading] = useState(true);

  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isRescheduleModalOpen, setIsRescheduleModalOpen] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [submittingCancel, setSubmittingCancel] = useState(false);
  const [submittingReview, setSubmittingReview] = useState(false);
  const [submittingReport, setSubmittingReport] = useState(false);
  // Cancellation charge preview — fetched when the cancel modal opens so
  // the customer sees what they'll be refunded vs. charged first.
  const [cancelPreview, setCancelPreview] = useState<CancellationPreview | null>(
    null,
  );
  const [cancelPreviewLoading, setCancelPreviewLoading] = useState(false);

  async function openCancelModal() {
    if (!info) return;
    setCancelPreview(null);
    setCancelPreviewLoading(true);
    setIsCancelModalOpen(true);
    try {
      setCancelPreview(await getCancellationPreview(info.id));
    } catch {
      // Non-fatal — the modal still lets them cancel; we just can't show
      // the charge breakdown. The cancel endpoint remains the source of
      // truth for what's actually charged.
      setCancelPreview(null);
    } finally {
      setCancelPreviewLoading(false);
    }
  }

  async function handleReportProblem(issue: string) {
    if (!info || !issue.trim()) return;
    setSubmittingReport(true);
    try {
      await reportBookingProblem(info.id, { message: issue.trim() });
      toast.success('Report sent — our team will be in touch.');
      setIsReportModalOpen(false);
    } catch (err) {
      toast.error(customerApiErrorMessage(err, 'Could not send your report.'));
    } finally {
      setSubmittingReport(false);
    }
  }

  async function load() {
    if (!id) return;
    setLoading(true);
    try {
      const res = await getBookingDetail(id);
      setInfo(res.info);
      // Reviews are only readable on COMPLETED bookings server-side
      // — for any other status the GET returns `info: null`. We
      // still call it and ignore failures so the page renders.
      try {
        const r = await getBookingReview(id);
        setReview(r.info ?? null);
      } catch {
        setReview(null);
      }
    } catch (err) {
      const msg = customerApiErrorMessage(err, 'Failed to load booking.');
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function handleCancel() {
    if (!info) return;
    setSubmittingCancel(true);
    try {
      await cancelBooking(info.id);
      setIsCancelModalOpen(false);
      navigate('/bookings');
    } catch (err) {
      alert(customerApiErrorMessage(err, 'Could not cancel.'));
    } finally {
      setSubmittingCancel(false);
    }
  }

  async function handleReview(payload: { rating: number; comment: string }) {
    if (!info) return;
    setSubmittingReview(true);
    try {
      await submitBookingReview(info.id, {
        rating: payload.rating,
        comment: payload.comment || null,
      });
      setIsReviewModalOpen(false);
      await load(); // refresh so the "Leave a review" button hides
    } catch (err) {
      alert(customerApiErrorMessage(err, 'Could not submit review.'));
    } finally {
      setSubmittingReview(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <MainHeader />
        <main className="max-w-[1140px] mx-auto px-4 py-16 text-center text-sm text-[#908e8e]">
          Loading booking…
        </main>
      </div>
    );
  }
  if (!info) {
    return (
      <div className="min-h-screen bg-white">
        <MainHeader />
        <main className="max-w-[1140px] mx-auto px-4 py-16 text-center">
          <p className="text-sm text-red-700 mb-4">
            Booking not found.
          </p>
          <PrimaryButton onClick={() => navigate('/bookings')}>
            Back to bookings
          </PrimaryButton>
        </main>
      </div>
    );
  }

  const currency = info.currency ?? 'GBP';
  // All-in (fee-inclusive) view of the saved snapshot — same presentation
  // as the basket so the customer sees the same numbers post-booking.
  const allIn = toAllInBreakdown(info);
  const address = [info.address_line1, info.city, info.postcode]
    .filter(Boolean)
    .join(' ');
  const proUser = info.professional?.user;
  const proName =
    joinName(proUser?.first_name ?? null, proUser?.last_name ?? null) ||
    info.professional?.business_name ||
    '';

  // Cancel only meaningful in pre-completed states. Server enforces
  // this too — see BookingService.cancel guards.
  const canCancel = ['CONFIRMED', 'IN_PROGRESS', 'PENDING_PAYMENT'].includes(
    info.status,
  );
  const canReview = info.status === 'COMPLETED' && !review;
  // Reschedule is offered on a confirmed booking with an assigned pro.
  // The 24h cutoff / max-count guardrails are enforced server-side; a
  // blocked attempt surfaces as a toast rather than hiding the button.
  const canReschedule =
    info.status === 'CONFIRMED' && !!info.professional?.id;
  // A pending reschedule sits PENDING_PROFESSIONAL_CONFIRM with the
  // original time stashed — show the awaiting-approval banner.
  const isReschedulePending =
    info.status === 'PENDING_PROFESSIONAL_CONFIRM' &&
    !!info.reschedule_original_start;

  // A live booking with no professional attached — either still being
  // matched, or the original pro rejected/timed-out and (because the
  // customer opted into a backup) it's parked awaiting reassignment. In
  // every one of these states `professional` is null, so instead of
  // hiding the pro block entirely we show a clear "matching you" card.
  // `pro_declined_at` tells us a pro previously bailed, so we can word
  // the message as a re-match rather than a first match.
  const isAwaitingPro =
    !info.professional &&
    ['PENDING_PROFESSIONAL_CONFIRM', 'CONFIRMED', 'IN_PROGRESS'].includes(
      info.status,
    );
  const proWasReassigned = !!info.pro_declined_at;

  return (
    <div className="min-h-screen bg-white">
      <MainHeader />

      <main className="max-w-[1140px] mx-auto px-4 py-8">
        {/* Top Section */}
        <div className="flex gap-6 mb-8">
          <button
            onClick={() => navigate(-1)}
            className="w-10 h-10 rounded-lg bg-[#f4f4f4] flex items-center justify-center hover:bg-[#eee] transition-colors shrink-0"
          >
            <span className="material-icons text-[#3a3335] text-[20px]">chevron_left</span>
          </button>

          <div className="flex-1">
            <h1 className="font-['Playfair_Display',sans-serif] font-bold text-[#8e2a2f] text-[20px] leading-[28px] mb-2">
              {address || 'Booking'}
            </h1>
            <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[16px] mb-1">
              {formatDateTime(info.scheduled_start)}
            </p>
            {info.created_at ? (
              <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[12px] mb-4">
                Placed on {formatDateTime(info.created_at)}
              </p>
            ) : null}
            <div className="flex flex-wrap items-center gap-2 mb-5">
              <span
                className="inline-flex items-center gap-1.5 h-[28px] px-3 rounded-full bg-[#fcfaf7] border border-[#ede8e9] font-['DM_Sans',sans-serif] text-[12px] font-bold text-[#3a3335]"
                title="Booking reference"
              >
                <span className="material-icons text-[14px] text-[#8e2a2f]">
                  confirmation_number
                </span>
                Ref&nbsp;
                <span className="font-mono">{info.reference}</span>
              </span>
              <StatusBadge status={info.status} />
            </div>

            {isReschedulePending ? (
              <div className="flex items-start gap-2 mb-5 p-3 rounded-[12px] bg-[#fcf6e9] border border-[#f0e0b8]">
                <span className="material-icons text-[18px] text-[#b8860b] mt-[1px]">
                  schedule
                </span>
                <p className="font-['DM_Sans',sans-serif] text-[#7a6010] text-[13px] leading-[19px]">
                  Reschedule requested — waiting for your pro to approve{' '}
                  <strong>{formatDateTime(info.scheduled_start)}</strong>. If
                  they can't make it, your booking stays at its original time.
                </p>
              </div>
            ) : null}

            <div className="flex flex-wrap gap-3">
              {canCancel ? (
                <PrimaryButton
                  className="px-8 py-3 !bg-[#8e2a2f]"
                  onClick={openCancelModal}
                >
                  Cancel booking
                </PrimaryButton>
              ) : null}
              {canReschedule ? (
                <SecondaryButton
                  className="px-6 !h-[48px]"
                  onClick={() => setIsRescheduleModalOpen(true)}
                  icon={
                    <span className="material-icons text-[18px]">
                      edit_calendar
                    </span>
                  }
                >
                  Reschedule
                </SecondaryButton>
              ) : null}
              <SecondaryButton
                className="px-6 !h-[48px]"
                onClick={() => handleDownloadInvoice(info)}
                icon={
                  <span className="material-icons text-[18px]">
                    file_download
                  </span>
                }
              >
                Download invoice
              </SecondaryButton>
            </div>
          </div>
        </div>


        <CancelBookingModal
          isOpen={isCancelModalOpen}
          onClose={() =>
            submittingCancel ? undefined : setIsCancelModalOpen(false)
          }
          onConfirm={handleCancel}
          preview={cancelPreview}
          previewLoading={cancelPreviewLoading}
        />

        {info.professional?.id ? (
          <RescheduleModal
            isOpen={isRescheduleModalOpen}
            onClose={() => setIsRescheduleModalOpen(false)}
            bookingId={info.id}
            professionalId={info.professional.id}
            durationMinutes={Number(info.duration_minutes ?? 0)}
            onDone={load}
          />
        ) : null}

        <ReportProblemModal
          isOpen={isReportModalOpen}
          onClose={() => (submittingReport ? undefined : setIsReportModalOpen(false))}
          onSubmit={handleReportProblem}
        />

        <LeaveReviewModal
          isOpen={isReviewModalOpen}
          onClose={() =>
            submittingReview ? undefined : setIsReviewModalOpen(false)
          }
          proName={proName}
          onSubmit={handleReview}
        />

        <div className="border-t border-[#ede8e9] pt-8 space-y-6">
          {/* Price Breakdown */}
          <section>
            <h2 className="font-['Playfair_Display',sans-serif] font-bold text-[#3a3335] text-[18px] mb-6">
              Price breakdown
            </h2>
            {allIn.items.map((item: any) => (
              <div key={item.id} className="flex justify-between items-start mb-3">
                <div>
                  <p className="font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-[16px]">
                    {item.name}
                  </p>
                  <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px]">
                    Qty {item.quantity} ·{' '}
                    {formatMoney(Number(item.customer_unit_price), currency)} each
                  </p>
                </div>
                <span className="font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-[16px]">
                  {formatMoney(Number(item.customer_line_total), currency)}
                </span>
              </div>
            ))}
          </section>

          {/* Totals breakdown — universal order:
                1. Item total
                2. Addon total
                3. Membership discount
                4. Coupon discount
                5. Tax
                6. Other charges
                7. Wallet
                8. Final total
              Items_total / addons_total are derived from `info.items`
              since the booking row doesn't store the split separately. */}
          {(() => {
            // All-in presentation: the platform fee + its VAT are folded
            // into the item total and shown only as an informational
            // "includes VAT" line; discounts are rescaled to the all-in
            // base. Lines still sum exactly to the stored total. See
            // toAllInBreakdown for the reconciliation.
            return (
              <section className="space-y-2">
                {allIn.itemsTotal > 0 ? (
                  <Row
                    label="Item total"
                    value={formatMoney(allIn.itemsTotal, currency)}
                  />
                ) : null}
                {allIn.addonsTotal > 0 ? (
                  <Row
                    label="Addon total"
                    value={formatMoney(allIn.addonsTotal, currency)}
                  />
                ) : null}
                {allIn.membershipDiscount > 0 ? (
                  <Row
                    label="Membership discount"
                    value={`− ${formatMoney(allIn.membershipDiscount, currency)}`}
                    negative
                  />
                ) : null}
                {allIn.voucherDiscount > 0 ? (
                  <Row
                    label="Coupon discount"
                    value={`− ${formatMoney(allIn.voucherDiscount, currency)}`}
                    negative
                  />
                ) : null}
                {allIn.legacyDiscount > 0 ? (
                  <Row
                    label={
                      allIn.isLegacyVoucher
                        ? 'Coupon discount'
                        : 'Membership discount'
                    }
                    value={`− ${formatMoney(allIn.legacyDiscount, currency)}`}
                    negative
                  />
                ) : null}
                {(info.charges_breakdown ?? []).map((c: any) => (
                  <Row
                    key={c.id}
                    label={c.name}
                    value={formatMoney(Number(c.amount), currency)}
                  />
                ))}
                {allIn.includedTax > 0 ? (
                  <Row
                    label={`${allIn.taxName} (on platform commission)`}
                    value={formatMoney(allIn.includedTax, currency)}
                  />
                ) : null}
                {allIn.walletAmount > 0 ? (
                  <Row
                    label="Wallet"
                    value={`− ${formatMoney(allIn.walletAmount, currency)}`}
                    negative
                  />
                ) : null}
              </section>
            );
          })()}

          {/* Total = what actually hit the customer's card AFTER the
              wallet portion was applied. The universal breakdown order
              renders the Wallet line above as a deduction, so this
              Total represents the residual charged to the card —
              `info.card_amount`. For a booking paid entirely from
              wallet, that's zero, which is correct: no card charge
              happened. */}
          <section className="flex justify-between items-center py-4 border-y border-[#ede8e9]">
            <span className="font-['Playfair_Display',sans-serif] font-bold text-[#3a3335] text-[18px]">
              Total
            </span>
            <span className="font-['Playfair_Display',sans-serif] font-bold text-[#3a3335] text-[24px]">
              {formatMoney(
                Number(
                  info.card_amount ??
                    Math.max(
                      0,
                      Number(info.total_amount ?? 0) -
                        Number(info.wallet_amount ?? 0),
                    ),
                ),
                currency,
              )}
            </span>
          </section>

          {/* Refund status — only when the booking actually went
              through the Stripe refund path. Wallet-only refunds
              don't set stripe_refund_status (the wallet ledger row
              is the source of truth and is already instant). The
              card-portion refund can sit at PENDING for 5-10
              business days while the bank settles it, then flip to
              SUCCEEDED or FAILED via the refund.* webhook. */}
          {info.stripe_refund_status ? (
            <RefundStatusCard
              status={info.stripe_refund_status}
              failureReason={info.stripe_refund_failure_reason}
              refundAmount={Number(info.refund_amount ?? 0)}
              currency={currency}
              syncedAt={info.stripe_refund_status_synced_at}
            />
          ) : null}

          {/* Existing review */}
          {review ? (
            <section className="p-4 rounded-xl bg-[#fcfaf7] border border-[#ede8e9]">
              <h2 className="font-['Playfair_Display',sans-serif] font-bold text-[#3a3335] text-[16px] mb-2">
                Your review
              </h2>
              <div className="flex items-center gap-1 mb-2">
                {Array.from({ length: 5 }).map((_, idx) => (
                  <span
                    key={idx}
                    className={`material-icons text-[20px] ${
                      idx < review.rating ? 'text-[#fbbf24]' : 'text-[#ede8e9]'
                    }`}
                  >
                    star
                  </span>
                ))}
              </div>
              {review.comment ? (
                <p className="font-['DM_Sans',sans-serif] text-[#3a3335] text-[14px] whitespace-pre-wrap">
                  {review.comment}
                </p>
              ) : null}
            </section>
          ) : null}

          {/* Assigned Pro — name, rating, tagline, contact shortcuts. */}
          {info.professional ? (
            <section>
              <h2 className="font-['Playfair_Display',sans-serif] font-bold text-[#3a3335] text-[18px] mb-6">
                Assigned pro
              </h2>
              <div className="bg-white border border-[#eee] rounded-xl p-5 mb-8 flex flex-col gap-5">
                <div className="flex items-start gap-4">
                  {/* Avatar resolution order — prefer the pro's own
                      profile photo (signed preview URL when the bucket
                      is private), fall back to their cover/portfolio
                      banner, then the canonical key, finally render
                      the initial on a brand cream tile. The previous
                      version only checked cover_image_url and rendered
                      the generic gray-shirt placeholder when null. */}
                  <div className="w-[80px] h-[80px] rounded-lg overflow-hidden shrink-0 bg-[#f2eee2] flex items-center justify-center">
                    {(() => {
                      const avatarSrc =
                        proUser?.profile_pic_preview_url ||
                        proUser?.profile_pic ||
                        info.professional.cover_image_preview_url ||
                        info.professional.cover_image_url ||
                        null;
                      return avatarSrc ? (
                        <img
                          src={avatarSrc}
                          alt={proName}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span className="font-['Playfair_Display',sans-serif] font-bold text-[#8e2a2f] text-[28px]">
                          {proName.slice(0, 1).toUpperCase() || '?'}
                        </span>
                      );
                    })()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-['Playfair_Display',sans-serif] font-bold text-[#3a3335] text-[18px] leading-[24px] mb-1 truncate">
                      {proName}
                    </h3>
                    {info.professional.business_name &&
                    proUser?.first_name &&
                    info.professional.business_name !== proName ? (
                      <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[13px] mb-1 truncate">
                        {joinName(proUser?.first_name ?? null, proUser?.last_name ?? null)}
                      </p>
                    ) : null}
                    {info.professional.tagline ? (
                      <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[13px] mb-2 truncate">
                        {info.professional.tagline}
                      </p>
                    ) : null}
                    <div className="flex items-center gap-1">
                      <span className="material-icons text-[#fbbf24] text-[18px]">
                        star
                      </span>
                      <span className="font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-[15px]">
                        {info.professional.rating_avg
                          ? Number(info.professional.rating_avg).toFixed(2)
                          : '—'}
                      </span>
                      <span className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[13px]">
                        ({info.professional.rating_count ?? 0})
                      </span>
                    </div>
                  </div>
                </div>

                {/* Contact details — surface the actual email + phone
                    inline so the customer doesn't have to click a
                    button just to copy the value. Each row stays
                    clickable (mailto: / tel:) so the existing
                    behaviour still works. */}
                {(proUser?.email || proUser?.mobile_number) ? (
                  <div className="flex flex-col gap-2 pt-4 border-t border-[#f0f0f0]">
                    {proUser?.email ? (
                      <a
                        href={`mailto:${proUser.email}`}
                        className="flex items-center gap-2 text-[#3a3335] hover:text-[#8e2a2f] transition-colors group break-all"
                      >
                        <span className="material-icons text-[#908e8e] group-hover:text-[#8e2a2f] text-[18px] shrink-0">
                          mail_outline
                        </span>
                        <span className="font-['DM_Sans',sans-serif] text-[14px]">
                          {proUser.email}
                        </span>
                      </a>
                    ) : null}
                    {proUser?.mobile_number ? (
                      <a
                        href={`tel:${[proUser.dial_code, proUser.mobile_number]
                          .filter(Boolean)
                          .join('')}`}
                        className="flex items-center gap-2 text-[#3a3335] hover:text-[#8e2a2f] transition-colors group"
                      >
                        <span className="material-icons text-[#908e8e] group-hover:text-[#8e2a2f] text-[18px] shrink-0">
                          call
                        </span>
                        <span className="font-['DM_Sans',sans-serif] text-[14px]">
                          {[proUser.dial_code, proUser.mobile_number]
                            .filter(Boolean)
                            .join(' ')}
                        </span>
                      </a>
                    ) : null}
                  </div>
                ) : null}

                {/* Message action — kept as a button (no inline value
                    to display; it opens the in-app chat). */}
                {proUser?.id ? (
                  <div className="flex flex-wrap gap-2 pt-4 border-t border-[#f0f0f0]">
                    <button
                      type="button"
                      onClick={() =>
                        navigate('/messenger', {
                          state: { openWithUserId: proUser.id },
                        })
                      }
                      className="inline-flex items-center gap-1.5 h-[40px] px-4 rounded-[10px] bg-[#8e2a2f] hover:bg-[#7a2228] text-white font-['DM_Sans',sans-serif] font-bold text-[13px] transition-colors"
                    >
                      <span className="material-icons text-[18px]">
                        chat_bubble_outline
                      </span>
                      Message
                    </button>
                  </div>
                ) : null}
              </div>

              {/* Bottom Actions */}
              <div className="flex flex-col md:flex-row gap-4">
                {canReview ? (
                  <SecondaryButton
                    className="flex-1 !h-[56px] !rounded-xl"
                    onClick={() => setIsReviewModalOpen(true)}
                  >
                    Leave a review
                  </SecondaryButton>
                ) : null}
                <SecondaryButton
                  className="flex-1 !h-[56px] !rounded-xl"
                  onClick={() => navigate('/')}
                >
                  Book again
                </SecondaryButton>
                <SecondaryButton
                  className="flex-1 !h-[56px] !rounded-xl"
                  onClick={() => setIsReportModalOpen(true)}
                >
                  Report pro
                </SecondaryButton>
              </div>
            </section>
          ) : isAwaitingPro ? (
            // No pro attached yet on a live booking — make the state
            // explicit instead of rendering a blank gap where the pro
            // card would be.
            <section>
              <h2 className="font-['Playfair_Display',sans-serif] font-bold text-[#3a3335] text-[18px] mb-6">
                Your professional
              </h2>
              <div className="bg-[#fcf6e9] border border-[#f0e0b8] rounded-xl p-5 flex items-start gap-4">
                <span className="material-icons text-[#b8860b] text-[24px] mt-0.5">
                  person_search
                </span>
                <div className="flex-1">
                  <h3 className="font-['Playfair_Display',sans-serif] font-bold text-[#3a3335] text-[16px] mb-1">
                    {proWasReassigned
                      ? "We're finding you a new professional"
                      : "We're matching you with a professional"}
                  </h3>
                  <p className="font-['DM_Sans',sans-serif] text-[#7a6010] text-[14px] leading-[20px]">
                    {proWasReassigned
                      ? "Your original professional is no longer able to attend, so our team is assigning a replacement. Your booking and payment are secure — we'll notify you as soon as your new pro is confirmed."
                      : "Your booking is confirmed and we're assigning the best available professional for your appointment. We'll notify you with their details as soon as they're confirmed."}
                  </p>
                </div>
              </div>
            </section>
          ) : null}
        </div>
      </main>

      <Footer />
    </div>
  );
}

// Maps a raw booking status enum to a human label + theme colours.
// Keeps the JSX clean — the status row is now a coloured pill instead
// of raw `PENDING_PROFESSIONAL_CONFIRM` shouting at the customer.
function StatusBadge({ status }: { status: string }) {
  const theme = statusTheme(status);
  return (
    <span
      className={`inline-flex items-center gap-1.5 h-[28px] px-3 rounded-full font-['DM_Sans',sans-serif] text-[12px] font-bold ${theme.cls}`}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: theme.dot }} />
      {theme.label}
    </span>
  );
}

function statusTheme(status: string): {
  label: string;
  cls: string;
  dot: string;
} {
  switch (status) {
    case 'PENDING_PAYMENT':
      return {
        label: 'Awaiting payment',
        cls: 'bg-[#fff7e6] text-[#9a6700] border border-[#fde2a1]',
        dot: '#d48806',
      };
    case 'PENDING_PROFESSIONAL_CONFIRM':
      return {
        label: 'Awaiting professional',
        cls: 'bg-[#fff7e6] text-[#9a6700] border border-[#fde2a1]',
        dot: '#d48806',
      };
    case 'CONFIRMED':
      return {
        label: 'Confirmed',
        cls: 'bg-[#e6f7ec] text-[#1f7a3a] border border-[#bce5c9]',
        dot: '#1f7a3a',
      };
    case 'IN_PROGRESS':
      return {
        label: 'In progress',
        cls: 'bg-[#e6f0ff] text-[#1d4fb8] border border-[#c2d4f5]',
        dot: '#1d4fb8',
      };
    case 'COMPLETED':
      return {
        label: 'Completed',
        cls: 'bg-[#e6f7ec] text-[#1f7a3a] border border-[#bce5c9]',
        dot: '#1f7a3a',
      };
    case 'CANCELLED_BY_CUSTOMER':
    case 'CANCELLED_BY_PRO':
    case 'CANCELLED_BY_ADMIN':
      return {
        label: 'Cancelled',
        cls: 'bg-[#f4f4f4] text-[#3a3335] border border-[#ede8e9]',
        dot: '#908e8e',
      };
    case 'REFUNDED':
      return {
        label: 'Refunded',
        cls: 'bg-[#f0e6ff] text-[#5b1aa6] border border-[#d8c2f5]',
        dot: '#5b1aa6',
      };
    case 'FAILED':
      return {
        label: 'Payment failed',
        cls: 'bg-[#fde8e8] text-[#a8071a] border border-[#fab8b8]',
        dot: '#a8071a',
      };
    case 'DISPUTED':
      return {
        label: 'Disputed',
        cls: 'bg-[#fde8e8] text-[#a8071a] border border-[#fab8b8]',
        dot: '#a8071a',
      };
    default:
      return {
        label: prettyStatus(status),
        cls: 'bg-[#f4f4f4] text-[#3a3335] border border-[#ede8e9]',
        dot: '#908e8e',
      };
  }
}

function prettyStatus(raw: string): string {
  return raw
    .toLowerCase()
    .split('_')
    .map((w) => w[0]?.toUpperCase() + w.slice(1))
    .join(' ');
}

// Customer-facing card explaining the lifecycle of a Stripe card
// refund. We surface this when stripe_refund_status is set (which
// only happens after fullyRefundBooking actually calls Stripe — the
// wallet-only path skips it entirely because the wallet ledger row
// is already the source of truth). Copy is deliberately reassuring
// — UK card refunds take 5-10 business days, and most customers
// otherwise assume the money's "lost" if it's not on the card
// statement same-day.
function RefundStatusCard({
  status,
  failureReason,
  refundAmount,
  currency,
  syncedAt,
}: {
  status: string;
  failureReason: string | null | undefined;
  refundAmount: number;
  currency: string;
  syncedAt: string | null | undefined;
}) {
  const theme = refundStatusTheme(status);
  return (
    <section
      className={`p-5 rounded-xl border ${theme.cls}`}
      role="status"
      aria-live="polite"
    >
      <div className="flex items-start gap-3">
        <span className={`material-icons text-[22px] ${theme.iconCls}`}>
          {theme.icon}
        </span>
        <div className="flex-1">
          <h2 className="font-['Playfair_Display',sans-serif] font-bold text-[#3a3335] text-[16px] mb-1">
            {theme.title}
          </h2>
          <p className="font-['DM_Sans',sans-serif] text-[#3a3335] text-[14px] leading-[20px] mb-2">
            {theme.body(formatMoney(refundAmount, currency))}
          </p>
          {status === 'FAILED' && failureReason ? (
            <p className="font-['DM_Sans',sans-serif] text-[#a8071a] text-[12px] mb-2">
              Reason from your bank: <code className="font-mono">{failureReason}</code>
            </p>
          ) : null}
          {syncedAt ? (
            <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[11px]">
              Last updated {formatDateTime(syncedAt)}
            </p>
          ) : null}
        </div>
      </div>
    </section>
  );
}

function refundStatusTheme(status: string): {
  cls: string;
  iconCls: string;
  icon: string;
  title: string;
  body: (amount: string) => string;
} {
  switch (status) {
    case 'SUCCEEDED':
      return {
        cls: 'bg-[#e6f7ec] border-[#bce5c9]',
        iconCls: 'text-[#1f7a3a]',
        icon: 'check_circle',
        title: 'Refund complete',
        body: (a) =>
          `${a} has been returned to the card you paid with. It may take a day or two to appear on your statement.`,
      };
    case 'FAILED':
      return {
        cls: 'bg-[#fde8e8] border-[#fab8b8]',
        iconCls: 'text-[#a8071a]',
        icon: 'error',
        title: 'Refund couldn\'t be completed',
        body: (a) =>
          `Your bank declined the ${a} refund. Our team has been notified and will reach out to issue the refund another way (wallet credit or a different card).`,
      };
    case 'CANCELED':
      return {
        cls: 'bg-[#f4f4f4] border-[#ede8e9]',
        iconCls: 'text-[#5a5c55]',
        icon: 'cancel',
        title: 'Refund cancelled',
        body: (a) =>
          `The ${a} refund was cancelled before it could be processed. Please contact support if this is unexpected.`,
      };
    case 'REQUIRES_ACTION':
      return {
        cls: 'bg-[#fff7e6] border-[#fde2a1]',
        iconCls: 'text-[#9a6700]',
        icon: 'pending',
        title: 'Refund needs a quick check',
        body: (a) =>
          `Your bank is asking for an extra step before completing the ${a} refund. We'll handle this with them — no action needed on your side.`,
      };
    case 'PENDING':
    default:
      return {
        cls: 'bg-[#f5f0e6] border-[#e8cd96]',
        iconCls: 'text-[#8e2a2f]',
        icon: 'schedule',
        title: 'Refund on its way',
        body: (a) =>
          `${a} is heading back to the card you paid with. UK card refunds typically take 5–10 business days to appear on your statement — we'll update this page when your bank confirms it.`,
      };
  }
}

// Build a print-ready invoice HTML document and open it in a new tab.
// Auto-fires the print dialog so the customer can save it as PDF — no
// backend round-trip needed.
function handleDownloadInvoice(info: any) {
  const currency = info.currency ?? 'GBP';
  // All-in (fee-inclusive) figures so the printed receipt matches the
  // on-screen breakdown and the basket.
  const allIn = toAllInBreakdown(info);
  const m = (n: number) => {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency,
        maximumFractionDigits: n % 1 === 0 ? 0 : 2,
      }).format(n);
    } catch {
      return `${currency} ${n.toFixed(2)}`;
    }
  };
  const proUser = info.professional?.user;
  const proName =
    [proUser?.first_name, proUser?.last_name].filter(Boolean).join(' ') ||
    info.professional?.business_name ||
    '—';
  const address = [
    info.address_line1,
    info.address_line2,
    info.city,
    info.postcode,
  ]
    .filter(Boolean)
    .join(', ');
  const when = info.scheduled_start
    ? new Date(info.scheduled_start).toLocaleString(undefined, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : '—';
  const itemRows = allIn.items
    .map(
      (it: any) => `
        <tr>
          <td>${escapeHtml(it.name ?? '')}</td>
          <td class="num">${it.quantity ?? 1}</td>
          <td class="num">${m(Number(it.customer_unit_price ?? 0))}</td>
          <td class="num">${m(Number(it.customer_line_total ?? 0))}</td>
        </tr>`,
    )
    .join('');
  const chargeRows = (info.charges_breakdown ?? [])
    .map(
      (c: any) => `
        <tr>
          <td colspan="3">${escapeHtml(c.name ?? '')}</td>
          <td class="num">${m(Number(c.amount ?? 0))}</td>
        </tr>`,
    )
    .join('');
  // Discount rows for the receipt (all-in / fee-inclusive, rescaled to the
  // all-in base) — universal order has them ABOVE the charges. Falls back
  // to a single combined row for legacy bookings written before the
  // membership / voucher columns existed.
  const discountRows = [
    allIn.membershipDiscount > 0
      ? `<tr><td class="label">Membership discount</td><td colspan="2"></td><td class="num">− ${m(allIn.membershipDiscount)}</td></tr>`
      : '',
    allIn.voucherDiscount > 0
      ? `<tr><td class="label">Coupon discount</td><td colspan="2"></td><td class="num">− ${m(allIn.voucherDiscount)}</td></tr>`
      : '',
    allIn.legacyDiscount > 0
      ? `<tr><td class="label">${allIn.isLegacyVoucher ? 'Coupon discount' : 'Membership discount'}</td><td colspan="2"></td><td class="num">− ${m(allIn.legacyDiscount)}</td></tr>`
      : '',
  ].join('');

  // All-in item / addon totals (pro rate × fee markup) — the platform fee
  // is folded into these, so there's no separate fee line. VAT is charged on
  // our commission and shown as its own line, ADDED to the total below.
  const itemsTotal = allIn.itemsTotal;
  const addonsTotal = allIn.addonsTotal;
  const includedTaxRow =
    allIn.includedTax > 0
      ? `<tr><td class="label">${escapeHtml(
          allIn.taxName,
        )} (on platform commission)</td><td colspan="2"></td><td class="num">${m(allIn.includedTax)}</td></tr>`
      : '';
  const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8" />
<title>Invoice ${escapeHtml(info.reference ?? '')}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif; color: #3a3335; margin: 40px; }
  .head { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #8e2a2f; padding-bottom: 16px; margin-bottom: 24px; }
  .brand { font-family: Georgia, serif; font-weight: 800; font-size: 28px; color: #8e2a2f; letter-spacing: -.02em; }
  .muted { color: #908e8e; font-size: 12px; }
  h2 { font-size: 14px; color: #8e2a2f; text-transform: uppercase; letter-spacing: .08em; margin: 24px 0 8px; }
  table { width: 100%; border-collapse: collapse; margin-top: 8px; font-size: 13px; }
  th, td { padding: 8px 10px; border-bottom: 1px solid #ede8e9; text-align: left; }
  th { background: #fcfaf7; font-weight: 700; }
  td.num, th.num { text-align: right; white-space: nowrap; }
  .totals td { border: none; padding: 4px 10px; }
  .totals .label { color: #908e8e; }
  .grand { font-size: 16px; font-weight: 700; border-top: 2px solid #3a3335; padding-top: 10px !important; }
  .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; font-size: 13px; }
  .info-grid p { margin: 4px 0; }
  .info-grid strong { color: #3a3335; }
  .footer { margin-top: 40px; font-size: 11px; color: #908e8e; border-top: 1px solid #ede8e9; padding-top: 12px; }
  @media print {
    body { margin: 20mm; }
    .no-print { display: none; }
  }
</style>
</head>
<body>
  <div class="head">
    <div>
      <div class="brand">Cherry Picked</div>
      <div class="muted">Booking invoice</div>
    </div>
    <div style="text-align:right">
      <div style="font-size:14px;font-weight:700">Ref ${escapeHtml(info.reference ?? '')}</div>
      <div class="muted">Issued: ${new Date().toLocaleDateString()}</div>
    </div>
  </div>

  <div class="info-grid">
    <div>
      <p><strong>Service</strong></p>
      <p>${escapeHtml(info.service?.name ?? '—')}</p>
      <p class="muted">${escapeHtml(info.service_category?.name ?? '')}</p>
    </div>
    <div>
      <p><strong>Professional</strong></p>
      <p>${escapeHtml(proName)}</p>
    </div>
    <div>
      <p><strong>When</strong></p>
      <p>${when}</p>
    </div>
    <div>
      <p><strong>Where</strong></p>
      <p>${escapeHtml(address || '—')}</p>
    </div>
  </div>

  <h2>Items</h2>
  <table>
    <thead>
      <tr>
        <th>Description</th>
        <th class="num">Qty</th>
        <th class="num">Unit</th>
        <th class="num">Amount</th>
      </tr>
    </thead>
    <tbody>${itemRows || `<tr><td colspan="4" class="muted">No line items.</td></tr>`}</tbody>
  </table>

  <h2>Totals</h2>
  <table class="totals">
    ${
      itemsTotal > 0
        ? `<tr><td class="label">Item total</td><td colspan="2"></td><td class="num">${m(itemsTotal)}</td></tr>`
        : ''
    }
    ${
      addonsTotal > 0
        ? `<tr><td class="label">Addon total</td><td colspan="2"></td><td class="num">${m(addonsTotal)}</td></tr>`
        : ''
    }
    ${discountRows}
    ${chargeRows}
    ${includedTaxRow}
    ${
      Number(info.wallet_amount) > 0
        ? `<tr><td class="label">Wallet</td><td colspan="2"></td><td class="num">− ${m(Number(info.wallet_amount))}</td></tr>`
        : ''
    }
    <tr><td class="grand label">Total</td><td colspan="2" class="grand"></td><td class="grand num">${m(
      Number(info.card_amount ?? Math.max(
        0,
        Number(info.total_amount ?? 0) - Number(info.wallet_amount ?? 0),
      )),
    )}</td></tr>
  </table>

  <p class="footer">This is a computer-generated receipt. Charged to card on file via Stripe.</p>
  <script>window.onload = function(){ setTimeout(function(){ window.print(); }, 250); };</script>
</body>
</html>`;
  const w = window.open('', '_blank');
  if (!w) {
    // Popup blocked — fall back to a data URL the user can open manually.
    window.location.href = `data:text/html;charset=utf-8,${encodeURIComponent(html)}`;
    return;
  }
  w.document.open();
  w.document.write(html);
  w.document.close();
}

function escapeHtml(s: string): string {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function Row({
  label,
  value,
  negative,
  muted,
}: {
  label: string;
  value: string;
  negative?: boolean;
  // Informational line (e.g. "Includes VAT") — lighter, not bold, since
  // it isn't added to the running total.
  muted?: boolean;
}) {
  return (
    <div className={`flex justify-between ${muted ? 'text-[13px] italic' : 'text-sm'}`}>
      <span className="text-[#908e8e]">{label}</span>
      <span
        className={`${
          muted
            ? 'text-[#908e8e] italic'
            : negative
              ? 'text-green-700 font-bold'
              : 'text-[#3a3335] font-bold'
        } font-['DM_Sans',sans-serif]`}
      >
        {value}
      </span>
    </div>
  );
}
